/**
 * Statische Icon-Map — die EINZIGE Stelle, an der aus 'lucide-react' importiert wird.
 *
 * WARUM: Ein Namespace-Import (`import * as L from 'lucide-react'`) macht alle
 * ~1500 Lucide-Icons un-tree-shakebar (~950 KB Quellcode im Entry-Chunk).
 * Diese Map listet exakt die Icons, die in der App vorkommen — der Rest von
 * Lucide wird dadurch beim Build entfernt.
 *
 * PFLEGE: Hier muss JEDES Icon stehen, das irgendwo als Name auftaucht:
 *   - `<Icon name="...">`-Literale und Ternaries (`name={x ? 'A' : 'B'}`) in apps/web/src
 *   - `icon: '...'`- und `icon="..."`-Literale (Sidebar-Items, KIND_ICON/VARIANT_ICON-Maps usw.)
 *   - Icon-Namen, die die API liefert (z. B. apps/api/src/skills/project-assistant/cockpit.ts
 *     für das Cockpit; Schema: packages/shared/src/schemas/cockpit.ts)
 *
 * ERWEITERN: Neuen Namen alphabetisch in Import UND Map eintragen.
 * Fehlt ein Name zur Laufzeit, rendert <Icon> den Fallback (HelpCircle)
 * und warnt im Dev-Modus — niemals ein Crash.
 *
 * REGENERIEREN (Inventur, vom Repo-Root):
 *   rg -oNU "<Icon[^>]*?name=\"([A-Za-z0-9]+)\"" apps/web/src -r '$1' --no-filename
 *   rg -oN "icon: '([A-Za-z0-9]+)'" apps/web/src apps/api/src packages/shared/src -r '$1' --no-filename
 *   zusätzlich Ternaries in `name={...}`, `icon="..."`-Props und Record<…, IconName>-Maps prüfen.
 */
import type { LucideIcon } from 'lucide-react';
import {
  Activity,
  AlarmClock,
  AlertCircle,
  AlertOctagon,
  AlertTriangle,
  Archive,
  ArrowDownToLine,
  ArrowLeft,
  ArrowRight,
  AtSign,
  BarChart3,
  Bell,
  BellOff,
  BookOpen,
  Box,
  Building2,
  CalendarCheck,
  CalendarDays,
  CalendarX,
  Camera,
  Car,
  Check,
  CheckCheck,
  CheckCircle2,
  CheckSquare,
  ChevronDown,
  ChevronRight,
  ChevronUp,
  Circle,
  Clipboard,
  ClipboardCopy,
  Clock,
  Contact2,
  Copy,
  Download,
  Droplets,
  Expand,
  ExternalLink,
  Eye,
  EyeOff,
  FileBox,
  FileCheck2,
  FileJson,
  FileQuestion,
  FileSpreadsheet,
  FileText,
  FileWarning,
  Flame,
  FolderClosed,
  FolderOpen,
  Gauge,
  GitBranch,
  GitCommit,
  GraduationCap,
  HelpCircle,
  History,
  Hourglass,
  Image,
  ImageOff,
  Inbox,
  Info,
  LayoutGrid,
  LayoutTemplate,
  LifeBuoy,
  Link2,
  ListChecks,
  Loader2,
  Lock,
  LogOut,
  Mail,
  MapPin,
  MapPinOff,
  Menu,
  MessageCircle,
  MessageSquare,
  Mic,
  MicOff,
  Minimize2,
  Moon,
  MousePointer2,
  MousePointerSquareDashed,
  Navigation,
  Pencil,
  PencilLine,
  Pentagon,
  PenTool,
  Phone,
  Play,
  PlayCircle,
  Plus,
  Printer,
  QrCode,
  RefreshCw,
  RotateCcw,
  RotateCw,
  Ruler,
  Save,
  Scale,
  Search,
  Send,
  Settings2,
  Shield,
  ShieldAlert,
  ShieldCheck,
  ShieldX,
  SkipForward,
  Smartphone,
  Sparkles,
  Square,
  Stamp,
  StickyNote,
  Sun,
  Table2,
  Tag,
  Target,
  ToggleRight,
  Trash2,
  Type,
  Upload,
  UploadCloud,
  User,
  UserCheck,
  UserCog,
  UserPlus,
  Users,
  UserX,
  Video,
  Volume2,
  VolumeX,
  X,
  XCircle,
  ZoomIn,
  ZoomOut,
} from 'lucide-react';

export const ICON_MAP = {
  Activity,
  AlarmClock,
  AlertCircle,
  AlertOctagon,
  AlertTriangle,
  Archive,
  ArrowDownToLine,
  ArrowLeft,
  ArrowRight,
  AtSign,
  BarChart3,
  Bell,
  BellOff,
  BookOpen,
  Box,
  Building2,
  CalendarCheck,
  CalendarDays,
  CalendarX,
  Camera,
  Car,
  Check,
  CheckCheck,
  CheckCircle2,
  CheckSquare,
  ChevronDown,
  ChevronRight,
  ChevronUp,
  Circle,
  Clipboard,
  ClipboardCopy,
  Clock,
  Contact2,
  Copy,
  Download,
  Droplets,
  Expand,
  ExternalLink,
  Eye,
  EyeOff,
  FileBox,
  FileCheck2,
  FileJson,
  FileQuestion,
  FileSpreadsheet,
  FileText,
  FileWarning,
  Flame,
  FolderClosed,
  FolderOpen,
  Gauge,
  GitBranch,
  GitCommit,
  GraduationCap,
  HelpCircle,
  History,
  Hourglass,
  Image,
  ImageOff,
  Inbox,
  Info,
  LayoutGrid,
  LayoutTemplate,
  LifeBuoy,
  Link2,
  ListChecks,
  Loader2,
  Lock,
  LogOut,
  Mail,
  MapPin,
  MapPinOff,
  Menu,
  MessageCircle,
  MessageSquare,
  Mic,
  MicOff,
  Minimize2,
  Moon,
  MousePointer2,
  MousePointerSquareDashed,
  Navigation,
  Pencil,
  PencilLine,
  Pentagon,
  PenTool,
  Phone,
  Play,
  PlayCircle,
  Plus,
  Printer,
  QrCode,
  RefreshCw,
  RotateCcw,
  RotateCw,
  Ruler,
  Save,
  Scale,
  Search,
  Send,
  Settings2,
  Shield,
  ShieldAlert,
  ShieldCheck,
  ShieldX,
  SkipForward,
  Smartphone,
  Sparkles,
  Square,
  Stamp,
  StickyNote,
  Sun,
  Table2,
  Tag,
  Target,
  ToggleRight,
  Trash2,
  Type,
  Upload,
  UploadCloud,
  User,
  UserCheck,
  UserCog,
  UserPlus,
  Users,
  UserX,
  Video,
  Volume2,
  VolumeX,
  X,
  XCircle,
  ZoomIn,
  ZoomOut,
} satisfies Record<string, LucideIcon>;

export type IconName = keyof typeof ICON_MAP;
